import React, { useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  InputAdornment,
  TextField,
  Typography,
  Theme,
  Button,
  IconButton,
  Alert,
} from '@mui/material';
import { makeStyles, WithStyles } from '@mui/styles';
import withStyles from '@mui/styles/withStyles';
import { reduxForm } from 'redux-form';
import { buildQaId } from 'utils/build-qa-id';
import { compose } from 'recompose';
import SearchIcon from '@mui/icons-material/Search';
import { notifyError } from 'actions/action-notifications';
import { useDispatch } from 'react-redux';
import LoadingOverlay from 'components/loading-overlay/loading-overlay';
import { ShippingClient } from 'clients/shipping-client';
import { buildHandleEnterKeyPress } from 'services/utils/accessibility';
import { PharmacyService } from 'services/utils/pharmacy-service';
import { useTypedSelector } from 'hooks/use-typed-selector';
import { FillDeliveryConfirmationStatus } from 'interfaces/enums/TaskStatuses/FillDeliveryConfirmationStatus';
import { windowFeatureIsEnabled } from 'config/window-features';
import { useTimeoutFn } from 'hooks/useTimeoutFn';
import { isEmpty } from 'lodash';
import Edit from '@mui/icons-material/Edit';
import EditOff from '@mui/icons-material/EditOff';
import DeliveryInformation from './delivery-information';
import ContentsOfPackage from './contents-of-package';
import ShippingVerification, {
  DebugScanButton,
  PulsingScannerIcon,
  ShippingLabelScan,
} from './shipping-verification';
import { AncillarySupplyValues, DeliveryMethodValue } from '../../constants/enums';
import PrintShippingButtons from './print-shipping-buttons';
import PaymentInformation from './payment-information';
import { getShippingScanStatus, isShippingPage, printLabelDetails } from './utils';
import { ComponentState, PackageLabelScanStatus, ShippingLabelScanStatus } from './types';
import { ShippingStyles } from './shipping.styles';
import PickupShippingButtons from './pickup-shipping-buttons';
import { useLabelScan } from './use-shipping-scan';
import { defaultTheme } from '../../lib/themes/arbor-theme-index';
import { ancillarySupplies } from '../../constants/lists';

type IShippingProps = WithStyles<typeof ShippingStyles>;
interface IStateProps {
  initialValues: '';
}
const initialSearchTerm = '';
type Props = IStateProps & IShippingProps;

const qaIdBuilder = buildQaId('shipping');

const useStyles = makeStyles((theme: Theme) => ({
  title: {
    backgroundColor: defaultTheme.palette.primary.bluesky,
    marginBottom: 20,
    minWidth: 600,
  },
  btnContainer: {
    marginTop: 30,
  },
  btn: {
    marginRight: 10,
  },
}));

const ModalForm = (props: any): JSX.Element => {
  const { close, title, instructions, clear } = props;
  const classes = useStyles();
  return (
    <Dialog open onClose={close} maxWidth="md">
      <DialogTitle id="simple-dialog-title" className={classes.title}>
        {title}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1">{instructions}</Typography>
        <Grid container className={classes.btnContainer}>
          <Grid item xs={6}>
            <Button className={classes.btn} onClick={close} variant="outlined">
              Yes
            </Button>
            <Button variant="contained" onClick={clear}>
              No
            </Button>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

const AncillarySupplyItem = ({ supplyLabel }: { supplyLabel: string }): JSX.Element => {
  return (
    <Grid item xs={12}>
      <Typography variant="body2" color="grey.500">
        {supplyLabel}
      </Typography>
    </Grid>
  );
};

export function Shipping(props: IShippingProps): JSX.Element {
  const { classes } = props;
  const dispatch = useDispatch();
  const [displayAncillarySupplyModal, setDisplayAncillarySupplyModal] = React.useState(false);
  const [displayedAncillarySupplyModal, setDisplayedAncillarySupplyModal] = React.useState(false);
  const [pharmacyService] = React.useState<PharmacyService>(new PharmacyService());
  const [orderIdSearchValue, setOrderIdSearchValue] = React.useState<string>(initialSearchTerm);
  const [currentOrderIdValue, setCurrentOrderIdValue] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [dataState, setDataState] = React.useState<ComponentState>();
  const [pharmacyHasACourierConfigured, setPharmacyHasACourierConfigured] =
    React.useState<boolean>(false);
  const [isLabLogistics, setIsLabLogistics] = React.useState<boolean>(false);
  const [isSpokeLogistics, setIsSpokeLogistics] = React.useState<boolean>(false);
  const [isAssociatedCourier, setIsAssociatedCourier] = React.useState<boolean>(false);
  const [isNuVizzCourier, setIsNuVizzCourier] = React.useState<boolean>(false);
  const [isFetchGoat, setIsFetchGoat] = React.useState<boolean>(false);
  const [isPhoxCourier, setIsPhoxCourier] = React.useState<boolean>(false);
  const orderIdInputRef = React.useRef<HTMLInputElement>(null);
  const packageButtonRef = React.useRef<HTMLDivElement>(null);
  const [showDuplicateOrderNotification, setShowDuplicateOrderNotification] =
    React.useState<boolean>(false);
  const [isDuplicateOrder, setIsDuplicateOrder] = React.useState<boolean>(false);
  const [enableManualInput, setEnableManualInput] = React.useState(false);
  const [pharmacyInfo, setPharmacyInfo] = React.useState<any>({});
  const [isCancelledFdcWarningVisible, setIsCancelledFdcWarningVisible] = React.useState(false);

  const currentMedicationInformation = dataState?.therapiesNames;
  const currentAncillarySupply = dataState?.ancillarySupplies;
  const currentDeliveryInformation = dataState?.deliveryInformation;
  const currentDeliveryMethod = currentDeliveryInformation?.deliveryMethod;
  const currentPrintInformation = dataState?.printInformation;
  const currentPaymentInformation = dataState?.paymentInformation;
  const currentFdcInformation = dataState?.fdcInformation;

  const neededSupplies = React.useMemo(() => {
    if (!currentAncillarySupply) {
      return [];
    }

    const supplies = Object.entries(currentAncillarySupply);
    return supplies.filter(item => {
      const [supplyName] = item;
      return ![
        AncillarySupplyValues.NotNeeded,
        AncillarySupplyValues.EasyOpenCaps,
        AncillarySupplyValues.AdherencePackaging,
      ].includes(supplyName as AncillarySupplyValues);
    });
  }, [currentAncillarySupply]);

  const isCurrentMethodOfDeliveryUps =
    currentDeliveryInformation?.deliveryMethod === DeliveryMethodValue.Ups;
  const isCurrentMethodOfDeliveryFedEx =
    currentDeliveryInformation?.deliveryMethod === DeliveryMethodValue.FedEx;
  const isCurrentMethodOfDeliveryCourier =
    currentDeliveryInformation?.deliveryMethod === DeliveryMethodValue.Courier;
  const isDelivery =
    isCurrentMethodOfDeliveryUps ||
    isCurrentMethodOfDeliveryFedEx ||
    (isCurrentMethodOfDeliveryCourier && isLabLogistics) ||
    (isCurrentMethodOfDeliveryCourier && isSpokeLogistics) ||
    (isCurrentMethodOfDeliveryCourier && isAssociatedCourier) ||
    (isCurrentMethodOfDeliveryCourier && isNuVizzCourier) ||
    (isCurrentMethodOfDeliveryCourier && isFetchGoat) ||
    (isCurrentMethodOfDeliveryCourier && isPhoxCourier);

  const isPickup = currentDeliveryInformation?.deliveryMethod === DeliveryMethodValue.PickUp;
  const willPickUp = currentFdcInformation?.statusId === FillDeliveryConfirmationStatus.WillPickUp;
  const isPacked = currentFdcInformation?.statusId !== FillDeliveryConfirmationStatus.Packed;

  const isShippingLabelPrinted =
    (!isDelivery && !isPickup) || (isDelivery && isPacked) || (isPickup && willPickUp);

  const users = useTypedSelector(state => state.lookups.users);
  const printedDate = currentPrintInformation?.created_dt;
  const createUserDisplayName = users.find(
    x => x.id === currentPrintInformation?.created_by,
  )?.display_name;
  const printDetails = printLabelDetails(createUserDisplayName, printedDate);
  const shippingBarcodeScanEnabled = windowFeatureIsEnabled('shipping_verification');

  const resetPageAfterScanComplete = useTimeoutFn(() => {
    if (!isPickup) setOrderIdSearchValue('');
    setCurrentOrderIdValue('');
    scanProps.setOrderId('');
    orderIdInputRef.current?.focus?.();
  });

  const highlightOrderIdInput = useTimeoutFn(() => {
    orderIdInputRef.current?.select?.();
  });

  const clear = () => {
    setOrderIdSearchValue(initialSearchTerm);
    setCurrentOrderIdValue(null);
  };

  useEffect(() => {
    if (
      !isEmpty(currentOrderIdValue) &&
      currentOrderIdValue !== null &&
      currentOrderIdValue !== ''
    ) {
      ShippingClient.fetchScanHistory(currentOrderIdValue || '').then(result => {
        const shippingScanStatus = getShippingScanStatus(result.data.history);
        const isDuplicate = shippingScanStatus === ShippingLabelScanStatus.Complete;
        setShowDuplicateOrderNotification(isDuplicate);
        setIsDuplicateOrder(isDuplicate);
      });
    }
  }, [currentOrderIdValue]);

  const getFCData = useCallback(
    async (orderId: string) => {
      setIsCancelledFdcWarningVisible(false);
      if (orderId == null || orderId === '') {
        dispatch(notifyError('Please enter an Order ID.'));
        return;
      }
      try {
        setCurrentOrderIdValue(null);
        setLoading(true);
        const response = await ShippingClient.fetch(orderId, 'shipping');
        if (response.data.error) {
          setCurrentOrderIdValue(null);
          dispatch(notifyError(response.data.error));
          highlightOrderIdInput.start(200);
        } else if (
          response.data.fdcInformation?.statusId &&
          [
            FillDeliveryConfirmationStatus.Canceled,
            FillDeliveryConfirmationStatus.CanceledDeceased,
          ].includes(response.data.fdcInformation?.statusId)
        ) {
          setIsCancelledFdcWarningVisible(true);
        } else {
          setDataState(response.data);
          const currentDispensingPharmacyNpi =
            response.data?.deliveryInformation?.dispensingPharmacyNpi;
          if (currentDispensingPharmacyNpi) {
            const pharmacyResponse = await pharmacyService.getPharmacyByNpi(
              currentDispensingPharmacyNpi,
            );
            setPharmacyInfo(pharmacyResponse || {});
            setPharmacyHasACourierConfigured(
              !!pharmacyResponse?.lab_logistics_customer_id ||
                !!pharmacyResponse?.spoke_logistics_customer_id ||
                !!pharmacyResponse?.associated_courier_customer_id ||
                !!pharmacyResponse?.nuvizz_courier_customer_id ||
                !!pharmacyResponse?.fetchgoat_customer_id ||
                !!pharmacyResponse?.phox_courier_customer_id,
            );
            setIsLabLogistics(pharmacyResponse?.lab_logistics_customer_id !== null);
            setIsSpokeLogistics(pharmacyResponse?.spoke_logistics_customer_id !== null);
            setIsAssociatedCourier(pharmacyResponse?.associated_courier_customer_id !== null);
            setIsNuVizzCourier(pharmacyResponse?.nuvizz_courier_customer_id !== null);
            setIsFetchGoat(pharmacyResponse?.fetchgoat_customer_id !== null);
            setIsPhoxCourier(pharmacyResponse?.phox_courier_customer_id !== null);
          }
          setCurrentOrderIdValue(orderId);
        }
      } catch (_error) {
        setCurrentOrderIdValue(null);
        dispatch(notifyError('Order ID does not exist. Please try again.'));
        highlightOrderIdInput.start(200);
      } finally {
        setLoading(false);
      }
    },
    [dispatch, highlightOrderIdInput, pharmacyService],
  );
  const updateData = async (orderId: string) => {
    try {
      const response = await ShippingClient.fetch(orderId, 'shipping');
      if (response.data.error) {
        setCurrentOrderIdValue(null);
        dispatch(notifyError(response.data.error));
        highlightOrderIdInput.start(200);
      } else {
        setDataState(response.data);
        const currentDispensingPharmacyNpi =
          response.data?.deliveryInformation?.dispensingPharmacyNpi;
        if (currentDispensingPharmacyNpi) {
          const pharmacyResponse = await pharmacyService.getPharmacyByNpi(
            currentDispensingPharmacyNpi,
          );
          setPharmacyInfo(pharmacyResponse || {});
          setPharmacyHasACourierConfigured(
            !!pharmacyResponse?.lab_logistics_customer_id ||
              !!pharmacyResponse?.spoke_logistics_customer_id ||
              !!pharmacyResponse?.associated_courier_customer_id ||
              !!pharmacyResponse?.nuvizz_courier_customer_id ||
              !!pharmacyResponse?.fetchgoat_customer_id ||
              !!pharmacyResponse?.phox_courier_customer_id,
          );
          setIsLabLogistics(pharmacyResponse?.lab_logistics_customer_id !== null);
          setIsSpokeLogistics(pharmacyResponse?.spoke_logistics_customer_id !== null);
          setIsAssociatedCourier(pharmacyResponse?.associated_courier_customer_id !== null);
          setIsNuVizzCourier(pharmacyResponse?.nuvizz_courier_customer_id !== null);
          setIsFetchGoat(pharmacyResponse?.fetchgoat_customer_id !== null);
          setIsPhoxCourier(pharmacyResponse?.phox_courier_customer_id !== null);
        }
        setCurrentOrderIdValue(orderId);
      }
    } catch (_error) {
      setCurrentOrderIdValue(null);
      dispatch(notifyError('Order ID does not exist. Please try again.'));
      highlightOrderIdInput.start(200);
    }
  };
  const updateShippingPage = async () => {
    updateData(currentOrderIdValue || '');
  };

  const handleChange = React.useCallback(
    (inputValue: string) => {
      setOrderIdSearchValue(inputValue);
    },
    [setOrderIdSearchValue],
  );

  const handleEnter = React.useCallback(
    (value?: string) => {
      const inputValue = value ?? orderIdSearchValue;
      const orderId =
        shippingBarcodeScanEnabled && inputValue.endsWith('-FC')
          ? inputValue.split('-FC')[0]
          : inputValue;
      if (!loading) {
        getFCData(orderId);
      }
    },
    [getFCData, loading, orderIdSearchValue, shippingBarcodeScanEnabled],
  );

  const scanProps = useLabelScan({
    async onOrderScan(orderId, labelValue, historyItem) {
      // When an order id is scanned, request the order data
      setOrderIdSearchValue(labelValue);
      handleEnter(labelValue);
    },
    async onShippingScan(orderId, labelValue, historyItem) {
      // When the shipping scan completes...
      if (historyItem.status_message === 'ok') {
        resetPageAfterScanComplete.start(400);
      }
    },
  });

  React.useEffect(() => {
    // After the order data is requested and has a valid response,
    // the `currentOrderIdValue` is set. This also sets the order id reference
    // in the label scan hook.
    if (!shippingBarcodeScanEnabled) return;
    if (currentOrderIdValue && currentOrderIdValue !== scanProps.orderId) {
      scanProps.setOrderId(currentOrderIdValue);
    }
    if (!currentOrderIdValue) {
      scanProps.setOrderId(null);
    }
  }, [shippingBarcodeScanEnabled, currentOrderIdValue, scanProps]);

  React.useEffect(() => {
    scanProps.setPackageScanRequirements(currentDeliveryInformation);
  }, [currentDeliveryInformation, scanProps.setPackageScanRequirements]);

  const showOrderIdScanPulse = React.useMemo(() => {
    if (!shippingBarcodeScanEnabled) return false;
    if (isPickup && scanProps.orderScanCompleted) return true;
    return (
      !currentOrderIdValue || scanProps.shippingScanStatus === ShippingLabelScanStatus.Complete
    );
  }, [shippingBarcodeScanEnabled, currentOrderIdValue, scanProps.shippingScanStatus]);

  const disableOrderIdInput = React.useMemo(() => {
    if (!shippingBarcodeScanEnabled) return false;
    if (isPickup) return false;
    return (
      !!currentOrderIdValue &&
      ((isPickup &&
        !(willPickUp && scanProps.packageScanStatus === PackageLabelScanStatus.Complete)) ||
        (isDelivery &&
          !(
            isPacked &&
            scanProps.packageScanStatus === PackageLabelScanStatus.Complete &&
            scanProps.shippingScanStatus === ShippingLabelScanStatus.Complete
          )))
    );
  }, [
    shippingBarcodeScanEnabled,
    currentOrderIdValue,
    scanProps.shippingScanStatus,
    scanProps.packageScanStatus,
    scanProps.orderScanCompleted,
    isPickup,
    willPickUp,
    isDelivery,
    isPacked,
  ]);

  React.useLayoutEffect(() => {
    if (!shippingBarcodeScanEnabled) return;
    if (disableOrderIdInput && scanProps.packageScanStatus === PackageLabelScanStatus.Complete) {
      const buttonEl = packageButtonRef.current?.querySelector('button');
      if (buttonEl) {
        buttonEl.focus?.();
      }
    }
  }, [shippingBarcodeScanEnabled, disableOrderIdInput, scanProps.packageScanStatus]);

  const renderOrderIdField = (): JSX.Element => {
    return (
      <div className={classes.container}>
        <Grid item xs={3}>
          <Typography className={classes.title}>Shipping Order Info</Typography>
        </Grid>
        <Grid item xs={3}>
          <LoadingOverlay open={loading} />
          <TextField
            variant="standard"
            name="shipping_order_info"
            label="Order ID"
            focused
            disabled={
              loading || disableOrderIdInput || (shippingBarcodeScanEnabled && !enableManualInput)
            }
            fullWidth
            value={orderIdSearchValue}
            onKeyPress={buildHandleEnterKeyPress(async (value?: string) => {
              const inputValue = value ?? orderIdSearchValue;
              if (!shippingBarcodeScanEnabled) {
                await getFCData(orderIdSearchValue);
              } else {
                scanProps.processScanInput(inputValue, true);
              }
            })}
            onChange={event => handleChange(event.target.value)}
            onClick={() => {
              orderIdInputRef.current?.select?.();
            }}
            placeholder={shippingBarcodeScanEnabled ? 'XXXXXXX-FC' : 'XXXXXXXXXXXXXXXX'}
            autoFocus={showOrderIdScanPulse}
            autoComplete="off"
            inputRef={orderIdInputRef}
            InputProps={{
              endAdornment: (
                <InputAdornment
                  position="end"
                  className={classes.searchIcon}
                  disablePointerEvents={loading}
                >
                  {shippingBarcodeScanEnabled && !disableOrderIdInput ? (
                    <IconButton onClick={() => setEnableManualInput(!enableManualInput)}>
                      {!enableManualInput ? <Edit /> : <EditOff />}
                    </IconButton>
                  ) : null}
                  {!shippingBarcodeScanEnabled ? (
                    <IconButton onClick={() => getFCData(orderIdSearchValue)}>
                      <SearchIcon />
                    </IconButton>
                  ) : null}
                  {showOrderIdScanPulse ? <PulsingScannerIcon /> : null}
                </InputAdornment>
              ),
            }}
            data-qa-id={qaIdBuilder('order-id-field')}
          />
          {isCancelledFdcWarningVisible && (
            <Alert severity="error" className={classes.fdcWarningMessage}>
              The FDC for the order number scanned is in cancelled status
            </Alert>
          )}
        </Grid>
      </div>
    );
  };

  const disableShippingButton =
    isShippingPage() && shippingBarcodeScanEnabled
      ? scanProps.packageScanStatus !== PackageLabelScanStatus.Complete
      : undefined;
  // Check current delivery methods for UPS, Courier and Internal Delivery
  const isUps = currentDeliveryMethod === DeliveryMethodValue.Ups;
  const isCourier = currentDeliveryMethod === DeliveryMethodValue.Courier;
  const isInternalDelivery = currentDeliveryMethod === DeliveryMethodValue.InternalDelivery;
  const isFedEx = currentDeliveryMethod === DeliveryMethodValue.FedEx;
  // Determine when to display the print label button based on delivery method and courier services
  const showDeliveryButtons =
    isInternalDelivery || isUps || isFedEx || (isCourier && pharmacyHasACourierConfigured);

  const isCourierWithMissingConfiguration = isCourier && !pharmacyHasACourierConfigured;

  const displayButtons = (orderId: string): JSX.Element | null => {
    if (isPickup) {
      return (
        <div ref={packageButtonRef}>
          <PickupShippingButtons
            currentDeliveryInformation={currentDeliveryInformation}
            currentPrintInformation={currentPrintInformation}
            orderId={orderId}
            printDetails={printDetails}
            currentFdcInformation={currentFdcInformation}
            disabled={false}
            pharmacyInfo={pharmacyInfo}
            updateShippingPage={updateShippingPage}
          />
        </div>
      );
    }
    if (showDeliveryButtons) {
      return (
        <>
          <div ref={packageButtonRef}>
            <PrintShippingButtons
              currentDeliveryInformation={currentDeliveryInformation}
              currentPrintInformation={currentPrintInformation}
              orderId={orderId}
              printDetails={printDetails}
              currentFdcInformation={currentFdcInformation}
              disabled={disableShippingButton}
              statusId={currentFdcInformation?.statusId}
              isDuplicateOrder={isDuplicateOrder}
              updateShippingPage={updateShippingPage}
            />
          </div>
        </>
      );
    }
    return null;
  };

  const orderCcCount = React.useMemo(() => {
    if (scanProps.orderId === currentOrderIdValue) {
      return currentDeliveryInformation?.ccPackageCount;
    }
    return undefined;
  }, [currentOrderIdValue, currentDeliveryInformation, scanProps.orderId]);

  const orderNrCount = React.useMemo(() => {
    if (scanProps.orderId === currentOrderIdValue) {
      return currentDeliveryInformation?.nrPackageCount;
    }
    return undefined;
  }, [currentOrderIdValue, currentDeliveryInformation, scanProps.orderId]);

  React.useEffect(() => {
    if (
      (neededSupplies.length > 0 ||
        currentDeliveryInformation?.welcomeKit ||
        currentDeliveryInformation?.privacyNotice) &&
      !displayedAncillarySupplyModal
    ) {
      setDisplayAncillarySupplyModal(true);
    }
  }, [orderCcCount, orderNrCount]);

  React.useEffect(() => {
    setDisplayedAncillarySupplyModal(displayedAncillarySupplyModal || displayAncillarySupplyModal);
  }, [displayAncillarySupplyModal]);

  React.useEffect(() => {
    if (currentOrderIdValue) {
      setDisplayedAncillarySupplyModal(false);
    }
  }, [currentOrderIdValue]);

  const renderShippingInformation = (orderId: string): JSX.Element => {
    return (
      <div className={classes.container}>
        {showDuplicateOrderNotification && (
          <ModalForm
            title="Caution"
            instructions="This order has already been completed, are you sure you want to continue ?"
            close={() => setShowDuplicateOrderNotification(false)}
            clear={() => clear()}
          />
        )}
        <ContentsOfPackage
          currentMedicationInformation={currentMedicationInformation}
          currentAncillarySupply={currentAncillarySupply}
          currentDeliveryInformation={currentDeliveryInformation}
          isPickup={isPickup}
          scanProps={shippingBarcodeScanEnabled ? scanProps : undefined}
        />
        <PaymentInformation currentPaymentInformation={currentPaymentInformation} />
        {!showDuplicateOrderNotification && shippingBarcodeScanEnabled && !isPickup ? (
          <ShippingVerification
            {...scanProps}
            currentAncillarySupply={currentAncillarySupply}
            currentDeliveryInformation={currentDeliveryInformation}
            orderCcCount={orderCcCount}
            orderNrCount={orderNrCount}
          />
        ) : null}
        <Grid className={classes.deliveryInformation}>
          <DeliveryInformation
            currentDeliveryInformation={currentDeliveryInformation}
            currentPrintInformation={currentPrintInformation}
            currentFdcInformation={currentFdcInformation}
            scanProps={shippingBarcodeScanEnabled ? scanProps : undefined}
            isMissingCourierConfiguration={isCourierWithMissingConfiguration}
            workflowType={pharmacyInfo?.workflow_type as number}
          />
        </Grid>
        {displayButtons(orderId)}
        {shippingBarcodeScanEnabled && !isPickup ? (
          <Grid
            component={ShippingLabelScan}
            {...scanProps}
            sx={{ padding: '48px 0' }}
            isShippingLabelPrinted={isShippingLabelPrinted}
          />
        ) : null}
        <Dialog
          open={
            displayAncillarySupplyModal &&
            scanProps.shippingScanStatus !== PackageLabelScanStatus.Complete &&
            windowFeatureIsEnabled('display_shipping_ancillary_supplies_modal')
          }
          maxWidth="sm"
        >
          <DialogTitle>
            This order contains Ancillary Supplies
            <Typography fontWeight={500}>
              Please confirm all Ancillary Supplies required for this order are included.
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Grid container>
              {neededSupplies.map(([key, val], idx) => {
                if (val === true) {
                  const supplyLabel = ancillarySupplies.find(supply => supply.value === key)?.label;
                  if (supplyLabel) {
                    return <AncillarySupplyItem supplyLabel={supplyLabel} key={key} />;
                  }
                }
                return <></>;
              })}
              {currentDeliveryInformation?.welcomeKit && (
                <AncillarySupplyItem supplyLabel="Welcome Kit" />
              )}
              {currentDeliveryInformation?.privacyNotice && (
                <AncillarySupplyItem supplyLabel="Privacy Notice" />
              )}
              <Grid item display="inline-flex" flexDirection="row-reverse" paddingTop={3} xs={12}>
                <Button variant="contained" onClick={() => setDisplayAncillarySupplyModal(false)}>
                  Ok
                </Button>
              </Grid>
            </Grid>
          </DialogContent>
        </Dialog>
      </div>
    );
  };

  return (
    <>
      {renderOrderIdField()}
      {currentOrderIdValue && renderShippingInformation(currentOrderIdValue)}
      {shippingBarcodeScanEnabled && <DebugScanButton />}
    </>
  );
}

export default compose<Props, IShippingProps>(
  withStyles(ShippingStyles),
  reduxForm({ form: 'shipping' }),
)(Shipping);
